import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const sections = [
  {
    id: 'overview',
    title: '1. Overview & Acceptance',
    content: `These Terms of Service ("Agreement") constitute a legally binding contract between Uni5d Technology Solutions LLC ("Uni5d," "Company," "we," "us," or "our"), a North Carolina limited liability company (EIN: 42-1896513), and the entity or individual ("Customer," "you," or "your") accessing or using the Quali5d AI Recruitment Intelligence platform ("Platform") at www.quali5d.com.

BY CLICKING "I AGREE," COMPLETING REGISTRATION, OR ACCESSING THE PLATFORM, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THIS AGREEMENT. IF YOU DO NOT AGREE, DO NOT ACCESS OR USE THE PLATFORM.

This Agreement is effective as of May 4, 2026 ("Effective Date"). We reserve the right to modify this Agreement at any time. Material changes will be communicated via email to the registered account email address at least 30 days before taking effect. Continued use of the Platform after the effective date of changes constitutes acceptance of the revised Agreement.`
  },
  {
    id: 'services',
    title: '2. Description of Services',
    content: `Quali5d provides an AI-powered recruitment intelligence platform consisting of three service phases:

Phase 1 — Requirement Intelligence: Upload job descriptions and audio/video intake recordings. Our AI analyzes and generates recruiter brief documents, candidate screeners, and targeted screening questions.

Phase 2 — AI Intake Calls: Automated AI-conducted intake calls with hiring managers via our AI interviewer ("Jessica"), generating structured requirement documents and transcripts.

Phase 3 — AI Candidate Interviews: Automated AI-conducted technical screening interviews with candidates via our AI interviewer ("Alex"), producing scored assessments, transcripts, and hiring recommendations.

All AI-generated outputs, scores, assessments, and recommendations are provided for informational and decision-support purposes only. They do not constitute employment decisions and must be reviewed and verified by qualified human professionals before any employment action is taken.`
  },
  {
    id: 'subscriptions',
    title: '3. Subscriptions & Payment',
    content: `3.1 Subscription Plans. Quali5d offers the following monthly subscription tiers: Essential ($99/month), Professional ($199/month), and Enterprise ($499/month). Each plan includes specific phase credits as described at www.quali5d.com/pricing.

3.2 Billing. Subscriptions are billed monthly in advance on the date of initial subscription ("Billing Date"). All fees are in US Dollars. Payment is processed through Stripe, Inc., our third-party payment processor.

3.3 Add-On Packs. Additional phase credits may be purchased as one-time add-on packs from your dashboard. Add-on credits are valid for 30 days from the date of purchase and are non-transferable between phases.

3.4 Taxes. Prices exclude applicable taxes. Customers in North Carolina are subject to NC state and local sales tax (currently 7.25% in Iredell County). Customers outside NC may be subject to applicable local taxes. Tax calculations are handled automatically via Stripe Tax.

3.5 Late Payment. Failure to pay fees when due will result in suspension of the Platform after a 7-day grace period. Accounts suspended for non-payment for more than 30 days may be terminated.

3.6 Price Changes. We reserve the right to change subscription prices upon 30 days' written notice. Price changes take effect on your next renewal date.`
  },
  {
    id: 'refunds',
    title: '4. Cancellation & Refund Policy',
    content: `4.1 Cancellation. You may cancel your subscription at any time through your account dashboard. Cancellation takes effect at the end of the current billing period. You will retain full access to the Platform until that date.

4.2 No Prorated Refunds. We do not issue prorated refunds for unused portions of a billing period. Add-on packs are non-refundable once purchased.

4.3 Exceptions. In cases of demonstrable Platform unavailability exceeding 99.5% monthly uptime SLA (Enterprise plan only), we may, at our sole discretion, issue service credits to your account. Service credits are not redeemable for cash.

4.4 Termination by Us. We reserve the right to terminate your account immediately for material breach of this Agreement, non-payment, or violation of our Acceptable Use Policy. In cases of termination for cause, no refunds will be issued.`
  },
  {
    id: 'data',
    title: '5. Data Ownership & Processing',
    content: `5.1 Customer Data Ownership. You retain all rights, title, and interest in and to any data, content, or materials you upload to the Platform ("Customer Data"), including job descriptions, audio/video recordings, candidate information, and company information.

5.2 License to Process. You grant Uni5d a limited, non-exclusive, royalty-free license to process, store, and analyze Customer Data solely for the purpose of providing the Platform services to you.

5.3 AI Processing. Customer Data may be processed by AI systems including, but not limited to, Anthropic's Claude API and ElevenLabs' conversational AI. By using the Platform, you authorize this processing.

5.4 Candidate Data. You are solely responsible for ensuring you have obtained all necessary consents from candidates before submitting their data to the Platform or scheduling them for AI interviews. You represent and warrant that all candidate data is collected and processed in compliance with applicable employment and data protection laws.

5.5 Data Retention. Audio and video recordings are retained for 90 days from the date of upload or interview completion, after which they are permanently deleted. Transcripts, documents, and assessment reports are retained for the duration of your subscription plus 30 days following termination.

5.6 Data Portability. Upon request prior to account termination, we will provide a data export of your Customer Data in a standard machine-readable format within 10 business days.

5.7 Data Deletion. Upon account termination, all Customer Data will be permanently deleted within 30 days, except where retention is required by applicable law.`
  },
  {
    id: 'ai',
    title: '6. AI Services & Limitations',
    content: `6.1 Advisory Nature. All AI-generated outputs, including but not limited to candidate scores, recommendations, assessments, and requirement documents, are advisory in nature. They are tools to assist human decision-makers and do not replace professional judgment.

6.2 No Employment Decisions. AI scores and recommendations generated by Quali5d must not be used as the sole basis for any employment decision, including hiring, rejection, promotion, or termination. Customers are solely responsible for ensuring their use of AI-generated outputs complies with all applicable employment, anti-discrimination, and AI regulation laws.

6.3 Regulatory Compliance. Customers operating in jurisdictions with AI employment screening regulations (including but not limited to New York City Local Law 144, the EU AI Act, and similar legislation) are solely responsible for conducting required bias audits, providing required disclosures to candidates, and maintaining compliance with applicable regulations. Uni5d does not provide compliance consulting services.

6.4 Recording Disclosure. Phase 2 and Phase 3 services involve AI-conducted conversations that are recorded and transcribed. You are responsible for disclosing to all hiring managers and candidates that their conversation will be conducted by an AI and will be recorded.

6.5 Accuracy. While we strive for accuracy, AI-generated content may contain errors, omissions, or inaccuracies. We make no warranty regarding the accuracy, completeness, or fitness for purpose of any AI-generated output.`
  },
  {
    id: 'ip',
    title: '7. Intellectual Property',
    content: `7.1 Platform IP. The Platform, including all software, algorithms, AI models, interfaces, and documentation, is and remains the exclusive intellectual property of Uni5d Technology Solutions LLC. This Agreement does not transfer any ownership rights in the Platform to you.

7.2 Feedback. Any feedback, suggestions, or ideas you provide regarding the Platform ("Feedback") may be used by Uni5d without restriction or compensation. You hereby assign to Uni5d all rights in such Feedback.

7.3 Aggregate Data. We may use anonymized, aggregated data derived from Platform usage (with all personally identifiable information removed) for product improvement, benchmarking, and research purposes.

7.4 Restrictions. You may not reverse engineer, decompile, disassemble, or attempt to derive the source code of the Platform. You may not resell, sublicense, or transfer your subscription to any third party.`
  },
  {
    id: 'confidentiality',
    title: '8. Confidentiality',
    content: `8.1 Each party agrees to maintain the confidentiality of the other party's non-public information disclosed in connection with this Agreement ("Confidential Information") using the same degree of care it uses to protect its own confidential information, but no less than reasonable care.

8.2 Confidential Information does not include information that: (a) is or becomes publicly known through no breach of this Agreement; (b) was already known to the receiving party before disclosure; (c) is independently developed without use of Confidential Information; or (d) is required to be disclosed by law or court order.

8.3 Customer credentials, API keys, and account information must be kept confidential by the Customer. Uni5d is not liable for unauthorized access resulting from Customer's failure to maintain credential confidentiality.`
  },
  {
    id: 'liability',
    title: '9. Limitation of Liability',
    content: `9.1 DISCLAIMER OF WARRANTIES. THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE." UNI5D DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.

9.2 LIMITATION OF LIABILITY. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, UNI5D'S TOTAL CUMULATIVE LIABILITY ARISING OUT OF OR RELATED TO THIS AGREEMENT SHALL NOT EXCEED THE TOTAL FEES PAID BY CUSTOMER TO UNI5D IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.

9.3 EXCLUSION OF CONSEQUENTIAL DAMAGES. IN NO EVENT SHALL UNI5D BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS, LOSS OF DATA, BUSINESS INTERRUPTION, OR REPUTATIONAL HARM, EVEN IF UNI5D HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.

9.4 ESSENTIAL BASIS. THE PARTIES ACKNOWLEDGE THAT THE LIMITATIONS OF LIABILITY IN THIS SECTION REFLECT A REASONABLE ALLOCATION OF RISK AND ARE AN ESSENTIAL ELEMENT OF THE BASIS OF THE BARGAIN BETWEEN THE PARTIES.`
  },
  {
    id: 'indemnification',
    title: '10. Indemnification',
    content: `You agree to indemnify, defend, and hold harmless Uni5d Technology Solutions LLC, its members, officers, employees, and agents from and against any claims, liabilities, damages, losses, costs, and expenses (including reasonable attorneys' fees) arising out of or related to: (a) your use of the Platform in violation of this Agreement; (b) your violation of any applicable law or regulation, including employment and data protection laws; (c) your failure to obtain required candidate consents; (d) any employment decision made based on AI-generated outputs; or (e) any third-party claim relating to your Customer Data.`
  },
  {
    id: 'governing',
    title: '11. Governing Law & Dispute Resolution',
    content: `11.1 Governing Law. This Agreement shall be governed by and construed in accordance with the laws of the State of North Carolina, without regard to its conflict of law provisions.

11.2 Disputes. Any dispute arising from or relating to this Agreement shall first be subject to good-faith negotiation between the parties for a period of 30 days. If not resolved, disputes shall be submitted to binding arbitration in Iredell County, North Carolina, administered by the American Arbitration Association under its Commercial Arbitration Rules.

11.3 Class Action Waiver. YOU AGREE THAT DISPUTES WILL BE RESOLVED ONLY ON AN INDIVIDUAL BASIS AND NOT IN A CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION.

11.4 Injunctive Relief. Nothing in this Section prevents either party from seeking injunctive or other equitable relief in a court of competent jurisdiction for the protection of intellectual property rights or confidential information.`
  },
  {
    id: 'general',
    title: '12. General Provisions',
    content: `12.1 Entire Agreement. This Agreement, together with the Privacy Policy and Acceptable Use Policy, constitutes the entire agreement between the parties regarding the Platform and supersedes all prior agreements.

12.2 Severability. If any provision of this Agreement is found invalid or unenforceable, the remaining provisions shall continue in full force.

12.3 Waiver. Failure to enforce any provision of this Agreement shall not constitute a waiver of future enforcement.

12.4 Force Majeure. Neither party shall be liable for delays or failures in performance resulting from circumstances beyond their reasonable control, including acts of God, natural disasters, government actions, or infrastructure failures.

12.5 Assignment. You may not assign this Agreement without our prior written consent. We may assign this Agreement in connection with a merger, acquisition, or sale of assets.

12.6 Notices. Legal notices must be sent to: Uni5d Technology Solutions LLC, Mooresville, North Carolina, via email to admin@uni5d.ai. We will send notices to the email address associated with your account.

12.7 Contact. For questions about this Agreement, contact us at admin@uni5d.ai.`
  }
];

export default function TermsPage() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Terms of Service — Quali5d AI Recruitment Platform</title>
        <meta name="description" content="Terms of service for Quali5d, operated by Uni5d Technology Solutions LLC. Service levels, payment terms, and usage requirements for AI recruitment software." />
        <link rel="canonical" href="https://www.quali5d.com/terms" />
        <meta property="og:title" content="Terms of Service — Quali5d AI Recruitment Platform" />
        <meta property="og:description" content="Terms of service for Quali5d, operated by Uni5d Technology Solutions LLC. Service levels, payment terms, and usage requirements for AI recruitment software." />
        <meta property="og:url" content="https://www.quali5d.com/terms" />
        <meta name="twitter:title" content="Terms of Service — Quali5d AI Recruitment Platform" />
        <meta name="twitter:description" content="Terms of service for Quali5d, operated by Uni5d Technology Solutions LLC. Service levels, payment terms, and usage requirements for AI recruitment software." />
      </Helmet>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
            </svg>
            <span className="text-sm font-medium">Back to Quali5d</span>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-xs">Q5</span>
            </div>
            <span className="font-bold text-gray-900 text-sm">Quali5d</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-4 py-1.5 mb-4">
            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 2a8 8 0 100 16A8 8 0 0010 2zm1 11H9v-2h2v2zm0-4H9V7h2v2z" clipRule="evenodd"/>
            </svg>
            <span className="text-xs font-semibold text-blue-700">Legal Document</span>
          </div>
          <h1 className="text-4xl font-black text-gray-900 mb-3">Terms of Service</h1>
          <p className="text-gray-500 text-lg">Subscription Agreement between you and Uni5d Technology Solutions LLC</p>
          <div className="flex flex-wrap gap-4 mt-4 text-sm text-gray-500">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-green-500 rounded-full"></span>Effective: May 4, 2026</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-blue-500 rounded-full"></span>Governing Law: North Carolina, USA</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-purple-500 rounded-full"></span>Entity: Uni5d Technology Solutions LLC</span>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Sidebar TOC */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24 bg-white rounded-2xl border border-gray-200 p-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 px-2">Contents</p>
              <nav className="space-y-0.5">
                {sections.map(s => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    onClick={() => setActiveSection(s.id)}
                    className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeSection === s.id
                        ? 'bg-blue-50 text-blue-700 font-semibold'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="space-y-6">
              {sections.map(s => (
                <div
                  key={s.id}
                  id={s.id}
                  className="bg-white rounded-2xl border border-gray-200 p-8"
                  onMouseEnter={() => setActiveSection(s.id)}
                >
                  <h2 className="text-xl font-bold text-gray-900 mb-4 pb-3 border-b border-gray-100">{s.title}</h2>
                  <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{s.content}</div>
                </div>
              ))}
            </div>

            {/* Footer note */}
            <div className="mt-8 bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="flex gap-3">
                <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                </svg>
                <div>
                  <p className="text-sm font-semibold text-amber-800 mb-1">Legal Notice</p>
                  <p className="text-sm text-amber-700">This document is provided for informational purposes. Quali5d recommends consulting qualified legal counsel for specific legal advice applicable to your jurisdiction and use case.</p>
                </div>
              </div>
            </div>

            <div className="mt-6 text-center text-sm text-gray-400">
              © 2026 Uni5d Technology Solutions LLC · EIN: 42-1896513 · Mooresville, North Carolina<br/>
              Questions? <a href="mailto:admin@uni5d.ai" className="text-blue-600 hover:underline">admin@uni5d.ai</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}